import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'
import { MenuItem } from 'primeng/api'
import {
    USER_ACTION_BLOCKED,
    USER_ACTION_LAUNCH_SNOOZE,
    USER_ACTION_REMOVE_SAVED,
    USER_ACTION_SAVED,
    USER_ACTION_UNBLOCKED,
    UserAction,
} from '../user-actions-menu/user-actions-menu.component'
import { Subject, take, takeUntil } from 'rxjs'
import { User } from '@app/core/_models'
import { DialogService } from '@app/core/_services/dialog.service'
import { UserRelationshipService } from '@app/core/_services/user-relationship.service'
import { UserService } from '@app/core/_services/user.service'
import { AnalyticsService } from '@app/core/_services/analytics.service'
import { MenuModule } from 'primeng/menu'
import {
    ContentStackItemOptions,
    ContentStackService,
} from '@app/core/_services/content-stack.service'
import { ButtonModule } from 'primeng/button'

@Component({
    selector: 'fm-actions-menu',
    imports: [ButtonModule, MenuModule],
    templateUrl: './actions-menu.component.html',
    styleUrl: './actions-menu.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActionsMenuComponent {
    @Input() classes: string = 'svg-user-action-menu-brand-1'
    @Input() showReportSession: boolean = false
    @Input() showReportProfile: boolean = false
    @Input() showBlock: boolean = true
    @Input() showUnblock: boolean = true
    @Input() showEditSnooze: boolean = true
    @Input() showSnooze: boolean = true
    @Input() showUnsnooze: boolean = true
    @Input() showSeeAvailability: boolean = false
    @Input() showViewProfile: boolean = true

    @Input() sessionTime: number | null = null
    @Input() sessionId: string | null = null
    @Input() timeZone: string | null = null
    @Input() targetUserDisplayName: string | null = null
    @Input() targetUserId: string | null = null
    @Input() targetUserProfileUrl: string | null = null
    @Input() targetUserPhotoUrl: string | null = null
    @Input() status: number | null = null
    @Input() source: string | null = null
    @Input() snoozeStatus: boolean = false
    @Input() snoozeExpiresAt: string | null = null
    @Input() saved: boolean = false
    @Input() sharedAvailability: boolean = false
    @Output() onUserAction = new EventEmitter<UserAction>()

    items: MenuItem[] | undefined

    private ngUnsubscribe: Subject<any> = new Subject<any>()
    private currentUser: User = new User()

    public showSeeAvailabilityOption: boolean = false
    public sectionOnePresent: boolean = false
    public sectionTwoPresent: boolean = false
    public sectionThreePresent: boolean = false
    public menuItemClasses: string[]

    constructor(
        private dialogService: DialogService,
        private userRelationshipService: UserRelationshipService,
        public userService: UserService,
        private analyticsService: AnalyticsService,
        private contentStackService: ContentStackService,
    ) {}

    ngOnInit() {
        this.userService.currentUser.pipe(take(1)).subscribe((user: User) => {
            this.currentUser = user
        })
        this.updateMenu()
    }

    ngOnChanges() {
        this.updateMenu()
    }

    public updateMenu() {
        const menuItems: MenuItem[] = []

        if (this.targetUserId == this.currentUser.userId) {
            this.menuItemClasses = ['svg-user-action-menu-item-disabled']
        } else {
            this.menuItemClasses = ['svg-user-action-menu-item']
        }

        this.showSeeAvailabilityOption = this.showSeeAvailability && this.saved
        this.sectionOnePresent = this.showSeeAvailabilityOption || this.showViewProfile
        this.sectionTwoPresent = this.showSnooze || this.showUnsnooze || this.showEditSnooze
        this.sectionThreePresent =
            this.showBlock || this.showUnblock || this.showReportProfile || this.showReportSession

        if (this.showSeeAvailabilityOption) {
            menuItems.push({
                label: 'See availability',
                icon: 'pi pi-calendar',
                disabled: !this.sharedAvailability,
                toolTip: 'Add tooltip logic',
                command: () => {
                    this.sendUserAvailabilityToDynamicPanel()
                },
            })
        }

        if (this.showViewProfile) {
            menuItems.push({
                label: 'View profile',
                icon: 'pi pi-id-card',
                toolTip: 'Add tooltip logic',
                command: () => {
                    this.sendProfileToDynamicPanel()
                },
            })
        }

        if (this.saved) {
            menuItems.push({
                label: 'Remove from favorites',
                icon: 'pi pi-star',
                toolTip: 'Add tooltip logic',
                command: () => {
                    this.removeFromSaved()
                },
            })
        }

        if (!this.snoozeStatus && this.showSnooze) {
            menuItems.push({
                label: 'Snooze',
                icon: 'pi pi-history',
                toolTip: 'Add tooltip logic',
                command: () => {
                    this.snooze(false)
                },
            })
        }
        if (this.snoozeStatus && this.showUnsnooze) {
            menuItems.push({
                label: 'Unsnooze',
                icon: 'pi pi-play',
                toolTip: 'Add tooltip logic',
                command: () => {
                    this.unsnooze()
                },
            })
        }

        if (this.snoozeStatus && this.showEditSnooze) {
            menuItems.push({
                label: 'Edit snooze',
                icon: 'pi pi-history',
                toolTip: 'Add tooltip logic',
                command: () => {
                    this.snooze(true)
                },
            })
        }

        if ((this.status === 0 || this.status === 1) && this.showBlock) {
            menuItems.push({
                label: 'Block',
                icon: 'pi pi-ban',
                toolTip: 'Add tooltip logic',
                command: () => {
                    this.openBlockUserDialog()
                },
            })
        }

        if ((this.status === 2 || this.status === 3) && this.showUnblock) {
            menuItems.push({
                label: 'Unblock',
                icon: 'pi pi-undo',
                toolTip: 'Add tooltip logic',
                command: () => {
                    this.unblockUser()
                },
            })
        }

        if (this.showReportSession) {
            menuItems.push({
                label: 'Report',
                icon: 'pi pi-flag',
                disabled: this.currentUser.userId == this.targetUserId,
                toolTip: 'Add tooltip logic',
                command: () => {
                    this.reportPartner()
                },
            })
        }

        if (this.showReportProfile) {
            menuItems.push({
                label: 'Report',
                icon: 'pi pi-flag',
                disabled: this.currentUser.userId == this.targetUserId,
                toolTip: 'Add tooltip logic',
                command: () => {
                    this.reportProfileViolation()
                },
            })
        }

        this.items = [...menuItems]
    }

    reportPartner() {
        this.dialogService
            .openReportBadBehavior(
                this.sessionId,
                this.sessionTime,
                this.targetUserDisplayName,
                this.source,
            )
            .afterClosed()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((reported) => {
                if (reported === true) {
                    this.dialogService.openReportBadBehaviorConfirmation(this.targetUserDisplayName)
                }
            })
    }

    public openBlockUserDialog() {
        this.dialogService
            .openBlockUserDialog(
                this.targetUserId,
                this.targetUserDisplayName,
                this.saved,
                this.source,
            )
            .afterClosed()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((userAction: UserAction) => {
                if (userAction.userAction === USER_ACTION_BLOCKED) {
                    this.onUserAction.emit(userAction)
                    this.dialogService
                        .openBlockUserConfirmation(this.targetUserDisplayName)
                        .afterClosed()
                        .pipe(takeUntil(this.ngUnsubscribe))
                        .subscribe(() => {})
                } else if (userAction.userAction === USER_ACTION_LAUNCH_SNOOZE) {
                    this.snooze(false)
                }
            })
    }

    public unblockUser() {
        this.userRelationshipService.unblockUser(this.targetUserId).subscribe(() => {
            this.onUserAction.emit({ userAction: USER_ACTION_UNBLOCKED })
            this.dialogService.openConfirmTransaction('Unblocked ' + this.targetUserDisplayName)
        })
    }

    reportProfileViolation() {
        this.dialogService
            .openReportProfile(
                this.targetUserProfileUrl,
                this.targetUserPhotoUrl,
                this.targetUserDisplayName,
                this.source,
            )
            .afterClosed()
            .pipe(take(1))
            .subscribe((reported) => {
                if (reported) {
                    this.dialogService.openConfirmTransaction('Report submitted!')
                }
            })
    }

    logSeeAvailability() {
        this.analyticsService.logClickedEvent('See availability', 'CSM - ' + this.source)
    }

    snooze(editSnooze: boolean) {
        this.dialogService
            .openSnoozeUserModal(
                this.targetUserId,
                this.targetUserDisplayName,
                editSnooze,
                this.snoozeExpiresAt,
                this.saved,
                this.source,
            )
            .afterClosed()
            .pipe(take(1))
            .subscribe((userAction: UserAction) => {
                this.onUserAction.emit(userAction)
            })
    }

    unsnooze() {
        this.dialogService
            .openUnsnoozeUserModal(this.targetUserId, this.targetUserDisplayName, this.source)
            .afterClosed()
            .pipe(take(1))
            .subscribe((userAction: UserAction) => {
                this.onUserAction.emit(userAction)
            })
    }

    addToSaved() {
        this.userRelationshipService
            .saveUser({ userId: this.targetUserId, saveUser: true })
            .subscribe(() => {
                this.onUserAction.emit({ userAction: USER_ACTION_SAVED })
                this.dialogService.openConfirmTransaction('Added to saved!')
            })
    }

    removeFromSaved() {
        this.userRelationshipService
            .saveUser({ userId: this.targetUserId, saveUser: false })
            .subscribe(() => {
                this.onUserAction.emit({ userAction: USER_ACTION_REMOVE_SAVED })
                this.dialogService.openConfirmTransaction('Removed from saved')
            })
    }

    viewProfile() {
        this.analyticsService.logClickedEvent('View profile', 'CSM - ' + this.source)
    }

    private sendProfileToDynamicPanel(): void {
        const options: ContentStackItemOptions = {
            popIfCovered: true,
            preserveStack: false,
        }

        this.contentStackService.pushProfileToDynamicPanel(this.targetUserProfileUrl, options)
    }

    private sendUserAvailabilityToDynamicPanel() {
        const options: ContentStackItemOptions = {
            popIfCovered: true,
            preserveStack: false,
            showRefreshButton: true,
        }

        this.contentStackService.pushUserAvailabilityToDynamicPanel(
            this.targetUserProfileUrl,
            options,
        )
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
    }
}
